/**
 * YNAB Budget Management Service
 * 
 * Provides core functionality for:
 * - Zero-based budgeting (every dollar assigned)
 * - Envelope allocation
 * - Category targets/goals
 * - Reallocation tracking
 * - Money age calculations
 * 
 * SECURITY NOTE:
 * - All queries filter by user_id for application-level security
 * - RLS policies are optional (works with or without)
 * - Safe for deployment with or without row-level security enabled
 */

import { supabase } from './supabaseClient';
import { format, startOfMonth } from 'date-fns';

const DELETE_GUARD_MESSAGE = 'Category contains allocated funds or transactions. Reassign funds to another category before deleting.';

/**
 * Error handler with RLS awareness
 */
function handleError(err, context = '') {
  if (err?.code === 'PGRST119') {
    throw new Error('Permission denied: You do not have access to this data');
  }
  if (err?.code === 'PGRST116') {
    return null;
  }
  throw err;
}

export async function handleAddCategory({
  userId,
  name,
  parentId = null,
  groupId = null,
  categoryKind = 'category',
  type = 'Expense',
  iconSlug = 'tag',
  sortOrder = 0
}) {
  const cleanName = name?.trim();
  if (!cleanName) {
    throw new Error('Category name is required');
  }

  if (!['group', 'category', 'subcategory'].includes(categoryKind)) {
    throw new Error('Invalid category kind');
  }

  try {
    const { data, error } = await supabase
      .from('categories')
      .insert({
        user_id: userId || null,
        parent_id: parentId || null,
        group_id: groupId || null,
        category_kind: categoryKind,
        name: cleanName,
        type,
        icon_slug: iconSlug || 'tag',
        sort_order: sortOrder,
        is_archived: false
      })
      .select('id, name, type, icon_slug, parent_id, group_id, category_kind, is_archived, sort_order')
      .single();

    if (error) {
      handleError(error, 'handleAddCategory');
    }

    return data;
  } catch (err) {
    throw err;
  }
}

export async function handleEditCategory(categoryId, updates = {}) {
  if (!categoryId) {
    throw new Error('categoryId is required');
  }

  const payload = {
    updated_at: new Date().toISOString()
  };

  if (updates.name !== undefined) payload.name = updates.name.trim();
  if (updates.parentId !== undefined) payload.parent_id = updates.parentId || null;
  if (updates.groupId !== undefined) payload.group_id = updates.groupId || null;
  if (updates.categoryKind !== undefined) payload.category_kind = updates.categoryKind;
  if (updates.type !== undefined) payload.type = updates.type;
  if (updates.iconSlug !== undefined) payload.icon_slug = updates.iconSlug || 'tag';
  if (updates.sortOrder !== undefined) payload.sort_order = updates.sortOrder;
  if (updates.isArchived !== undefined) payload.is_archived = Boolean(updates.isArchived);

  if (payload.name === '') {
    throw new Error('Category name is required');
  }

  try {
    const { data, error } = await supabase
      .from('categories')
      .update(payload)
      .eq('id', categoryId)
      .select('id, name, type, icon_slug, parent_id, group_id, category_kind, is_archived, sort_order')
      .single();

    if (error) {
      handleError(error, 'handleEditCategory');
    }

    return data;
  } catch (err) {
    throw err;
  }
}

export async function handleDeleteCategory(categoryId) {
  if (!categoryId) {
    throw new Error('categoryId is required');
  }

  try {
    const { data: allCategories, error: categoriesError } = await supabase
      .from('categories')
      .select('id, parent_id, group_id, is_archived');

    if (categoriesError) {
      handleError(categoriesError, 'handleDeleteCategory - categories');
    }

    const descendantIds = [];
    const collectDescendants = (parentId) => {
      (allCategories || [])
        .filter(category => !category.is_archived && (category.parent_id === parentId || category.group_id === parentId))
        .forEach(category => {
          if (!descendantIds.includes(category.id)) {
            descendantIds.push(category.id);
            collectDescendants(category.id);
          }
        });
    };
    collectDescendants(categoryId);

    const guardedIds = [categoryId, ...descendantIds];

    const { count: transactionCount, error: transactionError } = await supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .in('category_id', guardedIds);

    if (transactionError) {
      handleError(transactionError, 'handleDeleteCategory - category transactions');
    }

    const { count: subcategoryTransactionCount, error: subcategoryTransactionError } = await supabase
      .from('transactions')
      .select('id', { count: 'exact', head: true })
      .in('subcategory_id', guardedIds);

    if (subcategoryTransactionError) {
      handleError(subcategoryTransactionError, 'handleDeleteCategory - subcategory transactions');
    }

    const { count: allocationCount, error: allocationError } = await supabase
      .from('budget_categories')
      .select('id', { count: 'exact', head: true })
      .in('category_id', guardedIds)
      .or('allocated_amount.gt.0,spent_amount.gt.0');

    if (allocationError) {
      handleError(allocationError, 'handleDeleteCategory - allocations');
    }

    if (transactionCount > 0 || subcategoryTransactionCount > 0 || allocationCount > 0) {
      throw new Error(DELETE_GUARD_MESSAGE);
    }

    await supabase.from('budget_categories').delete().in('category_id', guardedIds);
    await supabase.from('category_targets').delete().in('category_id', guardedIds);

    const { error } = await supabase
      .from('categories')
      .delete()
      .in('id', guardedIds);

    if (error) {
      handleError(error, 'handleDeleteCategory - delete');
    }

    return { id: categoryId, deleted: true };
  } catch (err) {
    throw err;
  }
}

/**
 * Get or create budget for a given month
 * Works with or without RLS enabled
 * @param {string} userId - User ID (required for security)
 * @param {Date} dateInMonth - Any date in the target month
 * @returns {object} Budget record
 */
export async function getBudgetForMonth(userId, dateInMonth = new Date()) {
  if (!userId) {
    throw new Error('userId is required');
  }

  const monthStr = format(startOfMonth(dateInMonth), 'yyyy-MM-dd');
  
  try {
    // Query with user_id filter (works with/without RLS)
    const { data, error } = await supabase
      .from('budgets')
      .select('*')
      .eq('user_id', userId)
      .eq('month', monthStr)
      .maybeSingle(); // Returns null if not found instead of error
    
    if (error) {
      handleError(error, 'getBudgetForMonth - select');
    }

    // If budget exists, return it
    if (data) {
      return data;
    }

    // No budget exists, create one
    const { data: newBudget, error: createError } = await supabase
      .from('budgets')
      .insert({
        user_id: userId,
        month: monthStr,
        total_income: 0,
        total_allocated: 0,
        total_spent: 0,
        status: 'active'
      })
      .select()
      .single();
    
    if (createError) {
      handleError(createError, 'getBudgetForMonth - insert');
    }

    return newBudget;
  } catch (err) {
    throw err;
  }
}

/**
 * Get all budget categories for a budget
 * Filters by budget_id to ensure user access
 * @param {string} budgetId - Budget ID
 * @param {string} userId - User ID (for verification)
 * @returns {array} Array of budget categories with calculated fields
 */
export async function getBudgetCategories(budgetId, userId = null) {
  try {
    let query = supabase
      .from('budget_categories')
      .select(`
        *,
        category:categories(id, name, icon_slug, type)
      `)
      .eq('budget_id', budgetId)
      .order('priority_order', { ascending: true });

    const { data, error } = await query;
    
    if (error) {
      handleError(error, 'getBudgetCategories');
    }
    
    // Add calculated fields
    return (data || []).map(cat => ({
      ...cat,
      available_amount: (cat.allocated_amount || 0) - (cat.spent_amount || 0),
      spent_percentage: cat.allocated_amount > 0 ? (cat.spent_amount / cat.allocated_amount) * 100 : 0,
      is_over_budget: cat.spent_amount > cat.allocated_amount
    }));
  } catch (err) {
    throw err;
  }
}

/**
 * Allocate funds to a category (create or update envelope)
 * @param {string} budgetId - Budget ID
 * @param {string} categoryId - Category ID
 * @param {number} amount - Amount to allocate
 * @param {string} notes - Optional notes
 * @returns {object} Updated budget category
 */
export async function allocateToCategory(budgetId, categoryId, amount, notes = '') {
  if (!budgetId || !categoryId) {
    throw new Error('budgetId and categoryId are required');
  }

  if (typeof amount !== 'number' || amount < 0) {
    throw new Error('amount must be a non-negative number');
  }

  try {
    const { data, error } = await supabase
      .from('budget_categories')
      .upsert(
        {
          budget_id: budgetId,
          category_id: categoryId,
          allocated_amount: amount,
          notes: notes || null
        },
        { onConflict: 'budget_id,category_id' }
      )
      .select('*')
      .single();
    
    if (error) {
      handleError(error, 'allocateToCategory');
    }
    
    // Update budget totals
    await updateBudgetTotals(budgetId);
    
    return data;
  } catch (err) {
    throw err;
  }
}

/**
 * Reallocate funds between categories
 * Validates available funds before transfer
 * @param {string} budgetId - Budget ID
 * @param {string} fromCategoryId - Source category ID
 * @param {string} toCategoryId - Destination category ID
 * @param {number} amount - Amount to transfer
 * @param {string} reason - Reason for reallocation
 * @param {string} userId - User performing reallocation
 * @returns {object} Reallocation record
 */
export async function reallocateFunds(
  budgetId,
  fromCategoryId,
  toCategoryId,
  amount,
  reason = '',
  userId
) {
  if (!budgetId || !fromCategoryId || !toCategoryId || !userId) {
    throw new Error('budgetId, fromCategoryId, toCategoryId, and userId are required');
  }

  if (typeof amount !== 'number' || amount <= 0) {
    throw new Error('amount must be a positive number');
  }

  try {
    // Validate source has sufficient funds
    const { data: fromCat, error: fromError } = await supabase
      .from('budget_categories')
      .select('allocated_amount, spent_amount')
      .eq('budget_id', budgetId)
      .eq('category_id', fromCategoryId)
      .maybeSingle();
    
    if (fromError) {
      handleError(fromError, 'reallocateFunds - from_category select');
    }

    if (!fromCat) {
      throw new Error('Source category not found in this budget');
    }
    
    const available = (fromCat.allocated_amount || 0) - (fromCat.spent_amount || 0);
    if (available < amount) {
      throw new Error(`Insufficient available funds. Available: ${available}, Requested: ${amount}`);
    }
    
    // Deduct from source
    const newFromAllocation = (fromCat.allocated_amount || 0) - amount;
    const { error: updateFromError } = await supabase
      .from('budget_categories')
      .update({ allocated_amount: newFromAllocation })
      .eq('budget_id', budgetId)
      .eq('category_id', fromCategoryId);

    if (updateFromError) {
      handleError(updateFromError, 'reallocateFunds - update_source');
    }
    
    // Add to destination
    const { data: toCat, error: toError } = await supabase
      .from('budget_categories')
      .select('allocated_amount')
      .eq('budget_id', budgetId)
      .eq('category_id', toCategoryId)
      .maybeSingle();
    
    if (toError) {
      handleError(toError, 'reallocateFunds - to_category select');
    }
    
    const newToAllocation = (toCat?.allocated_amount || 0) + amount;
    const { error: updateToError } = await supabase
      .from('budget_categories')
      .upsert(
        {
          budget_id: budgetId,
          category_id: toCategoryId,
          allocated_amount: newToAllocation
        },
        { onConflict: 'budget_id,category_id' }
      );

    if (updateToError) {
      handleError(updateToError, 'reallocateFunds - update_destination');
    }
    
    // Record reallocation in audit trail
    const { data: reallocation, error: reallocError } = await supabase
      .from('reallocations')
      .insert({
        budget_id: budgetId,
        from_category_id: fromCategoryId,
        to_category_id: toCategoryId,
        amount,
        reason: reason || null,
        created_by: userId
      })
      .select()
      .single();
    
    if (reallocError) {
      handleError(reallocError, 'reallocateFunds - insert_reallocation');
    }
    
    // Update budget totals
    await updateBudgetTotals(budgetId);
    
    return reallocation;
  } catch (err) {
    throw err;
  }
}

/**
 * Get zero-based budget status
 * @param {string} budgetId - Budget ID
 * @returns {object} Status with unallocated funds and compliance flag
 */
export async function getZeroBasedStatus(budgetId) {
  if (!budgetId) {
    throw new Error('budgetId is required');
  }

  try {
    const { data: budget, error } = await supabase
      .from('budgets')
      .select('total_income, total_allocated, total_spent')
      .eq('id', budgetId)
      .maybeSingle();
    
    if (error) {
      handleError(error, 'getZeroBasedStatus');
    }

    if (!budget) {
      throw new Error('Budget not found');
    }
    
    const unallocated = (budget.total_income || 0) - (budget.total_allocated || 0);
    const tolerance = 0.01; // Allow for rounding
    
    return {
      totalIncome: budget.total_income || 0,
      totalAllocated: budget.total_allocated || 0,
      totalSpent: budget.total_spent || 0,
      unallocated: unallocated,
      isZeroBased: Math.abs(unallocated) < tolerance,
      remaining: unallocated > 0 ? unallocated : 0,
      overspent: unallocated < 0 ? Math.abs(unallocated) : 0
    };
  } catch (err) {
    throw err;
  }
}

/**
 * Create or update a spending target for a category
 * @param {string} userId - User ID
 * @param {string} categoryId - Category ID
 * @param {number} targetAmount - Target amount
 * @param {string} targetType - 'spend', 'save', or 'track'
 * @param {string} frequency - 'monthly', 'quarterly', 'annual'
 * @param {string} notes - Optional notes
 * @returns {object} Target record
 */
export async function setSpendingTarget(
  userId,
  categoryId,
  targetAmount,
  targetType = 'spend',
  frequency = 'monthly',
  notes = ''
) {
  if (!userId || !categoryId) {
    throw new Error('userId and categoryId are required');
  }

  if (typeof targetAmount !== 'number' || targetAmount < 0) {
    throw new Error('targetAmount must be a non-negative number');
  }

  try {
    // Check if target exists
    const { data: existing } = await supabase
      .from('category_targets')
      .select('id')
      .eq('user_id', userId)
      .eq('category_id', categoryId)
      .maybeSingle();
    
    if (existing) {
      // Update existing
      const { data, error } = await supabase
        .from('category_targets')
        .update({
          target_amount: targetAmount,
          target_type: targetType,
          frequency,
          notes: notes || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();
      
      if (error) {
        handleError(error, 'setSpendingTarget - update');
      }
      return data;
    } else {
      // Create new
      const { data, error } = await supabase
        .from('category_targets')
        .insert({
          user_id: userId,
          category_id: categoryId,
          target_amount: targetAmount,
          target_type: targetType,
          frequency,
          start_date: new Date().toISOString().split('T')[0],
          notes: notes || null
        })
        .select()
        .single();
      
      if (error) {
        handleError(error, 'setSpendingTarget - insert');
      }
      return data;
    }
  } catch (err) {
    throw err;
  }
}

/**
 * Get all spending targets for a user
 * @param {string} userId - User ID
 * @returns {array} Array of target records
 */
export async function getSpendingTargets(userId) {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const { data, error } = await supabase
      .from('category_targets')
      .select('*, category:categories(name, icon_slug)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      handleError(error, 'getSpendingTargets');
    }
    return data || [];
  } catch (err) {
    throw err;
  }
}

/**
 * Record FIFO money flow when an expense is saved.
 * Consumes the oldest available income first (YNAB Rule 4 FIFO queue),
 * inserts money_flow rows, and writes the weighted-average age back to
 * the expense transaction's money_age_days field.
 *
 * @param {string} userId - User ID
 * @param {{ id: string, date: string, debit: number }} expenseTx - Saved expense transaction
 */
export async function recordMoneyFlow(userId, expenseTx) {
  if (!userId || !expenseTx?.id || !(expenseTx.debit > 0)) return;

  try {
    // All income transactions, oldest first
    const { data: incomes, error: incomeErr } = await supabase
      .from('transactions')
      .select('id, date, credit')
      .eq('user_id', userId)
      .gt('credit', 0)
      .order('date', { ascending: true });

    if (incomeErr || !incomes || incomes.length === 0) return;

    // How much of each income has already been consumed
    const { data: existingFlows } = await supabase
      .from('money_flow')
      .select('income_transaction_id, amount')
      .eq('user_id', userId);

    const consumed = {};
    (existingFlows || []).forEach(f => {
      consumed[f.income_transaction_id] = (consumed[f.income_transaction_id] || 0) + (f.amount || 0);
    });

    // FIFO queue: income with remaining available balance
    const queue = incomes
      .map(inc => ({
        id: inc.id,
        date: inc.date,
        available: (inc.credit || 0) - (consumed[inc.id] || 0)
      }))
      .filter(inc => inc.available > 0);

    if (queue.length === 0) return;

    const expenseDate = new Date(expenseTx.date);
    let remaining = expenseTx.debit;
    const flowInserts = [];
    let totalWeightedAge = 0;
    let totalConsumed = 0;

    for (const inc of queue) {
      if (remaining <= 0) break;

      const toConsume = Math.min(remaining, inc.available);
      const incomeDate = new Date(inc.date);
      const ageInDays = Math.max(0, Math.round((expenseDate - incomeDate) / 86400000));

      flowInserts.push({
        user_id: userId,
        income_transaction_id: inc.id,
        expense_transaction_id: expenseTx.id,
        amount: toConsume,
        money_age_days: ageInDays
      });

      totalWeightedAge += ageInDays * toConsume;
      totalConsumed += toConsume;
      remaining -= toConsume;
    }

    if (flowInserts.length === 0) return;

    await supabase.from('money_flow').insert(flowInserts);

    const weightedAvgAge = totalConsumed > 0 ? Math.round(totalWeightedAge / totalConsumed) : 0;
    await supabase
      .from('transactions')
      .update({ money_age_days: weightedAvgAge })
      .eq('id', expenseTx.id);
  } catch (_err) {
    // Non-critical: age of money calc should never break the transaction save
  }
}

/**
 * Calculate average age of money (YNAB Rule 4)
 * @param {string} userId - User ID
 * @param {Date} dateInMonth - Month to calculate for
 * @returns {object} Age of money metrics
 */
export async function calculateAgeOfMoney(userId, dateInMonth = new Date()) {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const monthStart = format(startOfMonth(dateInMonth), 'yyyy-MM-dd');
    
    // Get transactions with money_age_days populated
    const { data: transactions, error } = await supabase
      .from('transactions')
      .select('debit, money_age_days')
      .eq('user_id', userId)
      .gte('date', monthStart)
      .gt('debit', 0); // Only expenses
    
    if (error) {
      handleError(error, 'calculateAgeOfMoney');
    }
    
    if (!transactions || transactions.length === 0) {
      return {
        ageOfMoney: 0,
        totalSpent: 0,
        transactionCount: 0,
        status: 'no-data'
      };
    }
    
    const totalSpent = transactions.reduce((sum, t) => sum + (t.debit || 0), 0);
    const ageSum = transactions.reduce((sum, t) => sum + (t.money_age_days || 0) * t.debit, 0);
    const ageOfMoney = totalSpent > 0 ? Math.round(ageSum / totalSpent) : 0;
    
    const goalAge = 30;
    const status = ageOfMoney >= goalAge ? 'on-track' : 'behind';
    
    return {
      ageOfMoney,
      totalSpent,
      transactionCount: transactions.length,
      goalAge,
      status,
      daysToGoal: Math.max(0, goalAge - ageOfMoney)
    };
  } catch (err) {
    throw err;
  }
}

/**
 * Record transaction with envelope allocation
 * Used when a transaction is created to update budget envelope
 * Non-critical operation - logs errors but doesn't fail main transaction
 * @param {string} budgetId - Budget ID
 * @param {string} categoryId - Category ID
 * @param {number} amount - Transaction amount
 * @returns {void}
 */
export async function recordTransactionToEnvelope(budgetId, categoryId, amount) {
  if (!budgetId || !categoryId) {
    return;
  }

  try {
    const { data: budgetCat } = await supabase
      .from('budget_categories')
      .select('spent_amount')
      .eq('budget_id', budgetId)
      .eq('category_id', categoryId)
      .maybeSingle();
    
    if (budgetCat) {
      const newSpent = (budgetCat.spent_amount || 0) + amount;
      const { error } = await supabase
        .from('budget_categories')
        .update({ spent_amount: newSpent, updated_at: new Date().toISOString() })
        .eq('budget_id', budgetId)
        .eq('category_id', categoryId);

      if (error && error.code !== 'PGRST119') {
        // Don't treat as fatal, but log
      }
      
      await updateBudgetTotals(budgetId);
    }
  } catch (err) {
    // Don't throw - this is auxiliary tracking
  }
}

/**
 * Update all calculated totals for a budget
 * @param {string} budgetId - Budget ID
 * @returns {void}
 */
async function updateBudgetTotals(budgetId) {
  try {
    const { data: categories } = await supabase
      .from('budget_categories')
      .select('allocated_amount, spent_amount')
      .eq('budget_id', budgetId);
    
    const totalAllocated = (categories || []).reduce(
      (sum, cat) => sum + (cat.allocated_amount || 0),
      0
    );
    const totalSpent = (categories || []).reduce(
      (sum, cat) => sum + (cat.spent_amount || 0),
      0
    );
    
    await supabase
      .from('budgets')
      .update({
        total_allocated: totalAllocated,
        total_spent: totalSpent,
        updated_at: new Date().toISOString()
      })
      .eq('id', budgetId);
  } catch (err) {
    // Don't throw - this is auxiliary tracking
  }
}

/**
 * Get reallocation history for a budget
 * @param {string} budgetId - Budget ID
 * @returns {array} Array of reallocation records
 */
export async function getAllocationHistory(budgetId) {
  if (!budgetId) {
    throw new Error('budgetId is required');
  }

  try {
    const { data, error } = await supabase
      .from('reallocations')
      .select(`
        *,
        from_category:categories!reallocations_from_category_id_fkey(name, icon_slug),
        to_category:categories!reallocations_to_category_id_fkey(name, icon_slug)
      `)
      .eq('budget_id', budgetId)
      .order('created_at', { ascending: false });
    
    if (error) {
      handleError(error, 'getAllocationHistory');
    }
    return data || [];
  } catch (err) {
    throw err;
  }
}
